import React, { createContext, useState, useContext, useEffect } from "react";
import { union, without } from "lodash";
type SearchFilterData = Record<string, string[]>;

type SearchFilterContextState = {
  data: Record<string, string[]>;
  setValue: (name: string, value: string, single: boolean) => void;
  remove: (name: string, value: string | string[]) => void;
  isSelected: (name: string, value: string) => boolean;
  submit: () => void;
  reset: () => void;
  sortSubmit: (sortOrder?: string, sortBy?: any) => void;
};

const SearchFilterContext = createContext<SearchFilterContextState | undefined>(
  undefined
);

export const SearchFilterProvider = (props: Props) => {
  const {
    children,
    initialValue,
    onChange,
    handleSubmit,
    handleReset,
    handleSortSubmit,
  } = props;
  const [state, setState] = useState<SearchFilterData>(initialValue);
  useEffect(() => {
    setState(initialValue);
  }, [initialValue]);
  const isSelected = (name: string, value: string | string[]) => {
    if (Array.isArray(value)) {
      return value.every((key) => state[name]?.includes(key));
    }

    if (value == null) {
      return state[name] == null;
    }

    return state[name]?.includes(value);
  };

  const setValue = (name: string, value: string | string[], single = false) => {
    const current = state[name];

    const valueAsArray = Array.isArray(value) ? value : [value];

    let newValue = [];

    if (isSelected(name, valueAsArray)) {
      newValue = without(current, ...valueAsArray);
    } else if (single) {
      newValue = valueAsArray;
    } else {
      newValue = union(current, valueAsArray);
    }

    const newState = {
      ...state,
      [name]: newValue,
    };

    setState(newState);
    onChange(newState);
  };
  const submit = async () => {
    // const getFilters = state;
    handleSubmit();
  };

  const sortSubmit = async (sortOrder?: string, sortBy?: string) => {
    // const getFilters = state;
    handleSortSubmit(sortOrder, sortBy);
  };

  const reset = async () => {
    // const getFilters = state;
    handleReset();
  };

  const remove = (key: string, toRemove: string | string[]) => {
    const current = state[key];
    const toRemoveAsArray = Array.isArray(toRemove) ? toRemove : [toRemove];

    onChange({
      ...state,
      [key]: without(current, ...toRemoveAsArray),
    });
  };

  const value = {
    data: state,
    setValue,
    isSelected,
    remove,
    submit,
    reset,
    sortSubmit,
  } as SearchFilterContextState;

  return (
    <SearchFilterContext.Provider value={value}>
      {children}
    </SearchFilterContext.Provider>
  );
};

type Props = {
  children: React.ReactNode;
  initialValue: Record<string, string[]>;
  onChange: (data: Record<string, string[]>) => void;
  handleSubmit?: () => void;
  handleReset?: () => void;
  handleSortSubmit?: (sortOrder?: string, sortBy?: any) => void;
};

export const useSearchFilterOption = ({ name, value, single = false }) => {
  const context = useContext(SearchFilterContext);

  if (context === undefined) {
    throw new Error(
      "useSearchFilterOption must be used within a SearchFilterProvider"
    );
  }

  return {
    checked: context.isSelected(name, value),
    onChange: () => {
      return context.setValue(name, value, single);
    },
  };
};

export const useSearchFiltersContext = () => {
  const context = useContext(SearchFilterContext);

  if (context === undefined) {
    throw new Error(
      "useSearchFiltersContext must be used within a SearchFilterProvider"
    );
  }

  return context;
};
